import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Upload, FileSpreadsheet, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import schedules from "@/api/schedules";
import { formatApiError } from "@/api/client";

/**
 * ScheduleUploadModal — drag&drop or file picker for the weekly schedule.
 *
 * Calls onUploaded(result) when the server has accepted the file.
 */
export default function ScheduleUploadModal({ open, onOpenChange, onUploaded }) {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setProgress(0);
    setResult(null);
    setError(null);
    setUploading(false);
  };

  const close = () => {
    if (uploading) return;
    reset();
    onOpenChange(false);
  };

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) {
      toast.error(t("schedule.upload.notXlsx"));
      return;
    }
    setFile(f);
    setError(null);
    setResult(null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    handleFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    setProgress(0);
    try {
      const r = await schedules.upload(file, (ev) => {
        if (ev.total) setProgress(Math.round((ev.loaded / ev.total) * 100));
      });
      setResult(r.data);
      // Phase 5.10 — toast names the week so the user sees the scope of the replace.
      toast.success(
        t("schedule.upload.successAtomic", {
          week: r.data?.week_start || "",
          n: r.data?.entries_count || 0,
        }),
      );
      onUploaded?.(r.data);
    } catch (err) {
      // Phase 5.10 — explicit reassurance: the previous schedule is intact.
      const detail = formatApiError(err);
      setError(detail);
      toast.error(t("schedule.upload.errorAtomic"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="max-w-lg"
        data-testid="schedule-upload-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            {t("schedule.upload.title")}
          </DialogTitle>
          <DialogDescription>
            {t("schedule.upload.description")}
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition ${
              dragging
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary/50"
            }`}
            data-testid="schedule-upload-dropzone"
          >
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="schedule-upload-input"
            />
            {file ? (
              <div className="flex items-center justify-center gap-2 text-sm">
                <FileSpreadsheet className="h-4 w-4 text-emerald-500" />
                <span className="font-medium">{file.name}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                  className="rounded p-1 hover:bg-muted"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="mx-auto h-8 w-8 text-muted-foreground" />
                <div className="text-sm font-medium">
                  {t("schedule.upload.dropOrClick")}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t("schedule.upload.formatHint")}
                </div>
              </div>
            )}
          </div>
        )}

        {uploading && (
          <div className="space-y-2">
            <Progress value={progress} />
            <div className="text-center text-xs text-muted-foreground">
              {progress}%
            </div>
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t("common.error")}</AlertTitle>
            <AlertDescription className="text-xs">{error}</AlertDescription>
          </Alert>
        )}

        {result && (
          <Alert>
            <FileSpreadsheet className="h-4 w-4" />
            <AlertTitle>{t("schedule.upload.success", { n: result.entries_count })}</AlertTitle>
            <AlertDescription className="space-y-1 text-xs">
              <div>
                <strong>{t("schedule.upload.week")}:</strong> {result.week_start} → {result.week_end}
              </div>
              <div>
                <strong>{t("schedule.upload.sections")}:</strong>{" "}
                {(result.sections_seen || []).join(", ")}
              </div>
              {result.unmatched_employees?.length > 0 && (
                <div className="mt-2 rounded border border-amber-500/30 bg-amber-500/5 p-2">
                  <div className="font-medium text-amber-500">
                    ⚠️ {t("schedule.upload.unmatched", { n: result.unmatched_employees.length })}
                  </div>
                  <div className="mt-1 text-[10px] text-muted-foreground">
                    {result.unmatched_employees.slice(0, 8).join(" · ")}
                    {result.unmatched_employees.length > 8 ? " …" : ""}
                  </div>
                </div>
              )}
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="ghost" onClick={close} disabled={uploading}>
                {t("common.cancel")}
              </Button>
              <Button
                onClick={handleUpload}
                disabled={!file || uploading}
                data-testid="schedule-upload-submit"
              >
                <Upload className="mr-2 h-4 w-4" />
                {uploading ? t("common.loading") : t("schedule.upload.submit")}
              </Button>
            </>
          ) : (
            <Button onClick={close} data-testid="schedule-upload-done">
              {t("common.done")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
